import { EducatorPageChrome } from "@/pages/educators/page-chrome"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { PageBody } from "@/components/ui/layout"
import { Resources } from "i18next"
import { MessageSquareText, Star } from "lucide-react"
import { useTranslation } from "react-i18next"
import {
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
  EmptyDescription,
  Empty,
} from "@/components/ui/empty"
import { EducatorsFeedbacksTeacherMeasurement } from "@/types/serializers"
import { Progress } from "@/components/ui/progress"
import { useProps } from "@/lib/page-rendering"

type ShowProps = {
  measurement: EducatorsFeedbacksTeacherMeasurement
}
const useShowProps = () => useProps<ShowProps>()
const averageFormatter = new Intl.NumberFormat(undefined, {
  style: "decimal",
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
})

export default function Page() {
  const { t } = useTranslation("teacher_feedbacks")
  const {
    measurement: { feedback, comments, self_assessment },
  } = useShowProps()

  return (
    <>
      <EducatorPageChrome />
      <PageBody>
        <Card>
          <CardHeader>
            <CardTitle>{t("show.self_assessment")}</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 gap-2 sm:grid-cols-3 lg:grid-cols-5">
            {Object.entries(self_assessment).map(([category, assessment]) => (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-xl">
                    {t(
                      `categories.${category as keyof Resources["teacher_feedbacks"]["categories"]}`,
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent className="flex flex-row flex-wrap items-center gap-2">
                  <StarDisplay rating={assessment ?? 0} />
                  <span>
                    ({t("average_rating", { average: assessment, max: 5 })})
                  </span>
                </CardContent>
              </Card>
            ))}
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>{t("show.feedback_title")}</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {Object.entries(feedback).map(([category, ratings]) => (
              <Card key={category}>
                <CardHeader>
                  <CardTitle>
                    {t(
                      `categories.${category as keyof Resources["teacher_feedbacks"]["categories"]}`,
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <Rating ratings={ratings} />
                </CardContent>
              </Card>
            ))}
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>{t("show.comments")}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4" role="">
            {comments.length === 0 ? (
              <Empty>
                <EmptyHeader>
                  <EmptyMedia
                    variant="icon"
                    className="text-muted size-20 bg-white"
                  >
                    <MessageSquareText className="size-16" />
                  </EmptyMedia>
                  <EmptyTitle>{t("show.empty_commnents_title")}</EmptyTitle>
                  <EmptyDescription>
                    {t("show.empty_commnents_description")}
                  </EmptyDescription>
                </EmptyHeader>
              </Empty>
            ) : (
              comments.map(
                ({ student_full_name, average_rating, comment }, index) => (
                  <Card key={index}>
                    <CardHeader className="flex flex-row flex-wrap justify-between">
                      <CardTitle>{student_full_name}</CardTitle>
                      <CardDescription className="flex flex-row items-center gap-2">
                        <StarDisplay rating={average_rating} />
                        <span className="text-muted-foreground text-sm">
                          (
                          {t("average_rating", {
                            average: averageFormatter.format(average_rating),
                            max: "5.0",
                          })}
                          )
                        </span>
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <p className="">{comment}</p>
                    </CardContent>
                  </Card>
                ),
              )
            )}
          </CardContent>
        </Card>
      </PageBody>
    </>
  )
}

function StarDisplay({
  rating,
  maxStars = 5,
}: {
  rating: number
  maxStars?: number
}) {
  return (
    <div className="flex items-center gap-0.5">
      {Array.from({ length: maxStars }, (_, i) => {
        const starValue = i + 1
        const isFilled = starValue <= Math.floor(rating)
        const isPartial =
          starValue > Math.floor(rating) && starValue < Math.ceil(rating) + 1
        const fillPercentage = isPartial ? (rating % 1) * 100 : 0

        return (
          <div key={i} className="relative">
            <Star className="h-5 w-5 text-gray-200" fill="currentColor" />
            {(isFilled || isPartial) && (
              <div
                className="absolute inset-0 overflow-hidden"
                style={{ width: isFilled ? "100%" : `${fillPercentage}%` }}
              >
                <Star
                  className="text-brand-philo-mustard h-5 w-5"
                  fill="currentColor"
                />
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

function Rating({
  ratings,
}: {
  ratings: Array<{
    rating: string
    count: number
  }>
}) {
  const { t } = useTranslation("teacher_feedbacks")
  const totalCount = ratings.reduce((acc, curr) => acc + curr.count, 0)

  const averageScore =
    totalCount > 0
      ? ratings.reduce(
          (acc, curr) => acc + curr.count * Number(curr.rating),
          0,
        ) / totalCount
      : 0

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <StarDisplay rating={averageScore} />
        <span className="text-lg font-semibold">
          {t("average_rating", {
            average: averageFormatter.format(averageScore),
            max: "5.0",
          })}
        </span>
        <span className="text-muted-foreground text-sm">
          ({t("ratings_count", { count: totalCount })})
        </span>
      </div>

      <div className="space-y-2">
        {ratings.map(({ rating, count }) => {
          const percentage = totalCount > 0 ? (count / totalCount) * 100 : 0

          return (
            <div
              key={rating}
              className="grid grid-cols-[3rem_2fr_6rem] items-center gap-x-2 text-sm"
            >
              <span className="text-muted-foreground">
                {t("stars", { count: Number(rating) })}
              </span>
              <Progress value={percentage} />
              <span className="text-muted-foreground">
                ({t("ratings_count", { count })})
              </span>
            </div>
          )
        })}
      </div>
    </div>
  )
}
